<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增等級
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filterActive" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="true">啟用</option>
          <option value="false">停用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchTiers">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>等級代碼</th>
            <th>等級名稱</th>
            <th>顯示順序</th>
            <th>最低消費金額</th>
            <th>最低消費次數</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tier in tiers" :key="tier.id">
            <td>
              <code class="code-badge">{{ tier.tierCode }}</code>
            </td>
            <td>
              <span v-if="tier.color" class="tier-name" :style="{ color: tier.color }">
                {{ tier.tierName }}
              </span>
              <span v-else>{{ tier.tierName }}</span>
            </td>
            <td>{{ tier.displayOrder }}</td>
            <td>{{ formatCurrency(tier.minSpentAmount) }}</td>
            <td>{{ tier.minPurchaseCount }} 次</td>
            <td>
              <span :class="tier.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ tier.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-secondary" @click="handleEdit(tier.id)">
                  <i class="ri-edit-line"></i>
                  編輯
                </button>
                <button class="btn btn-sm btn-danger" @click="handleDelete(tier.id)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 降等前提醒設定 -->
    <div class="reminder-card">
      <div class="reminder-card-header">
        <div>
          <h2 class="reminder-card-title">
            <i class="ri-notification-3-line"></i>
            降等前提醒
          </h2>
          <p class="reminder-card-desc">
            年度結算日前自動通知未達續等門檻的會員「再消費 $X 即可保級」。
          </p>
        </div>
        <div class="reminder-card-actions">
          <button class="btn btn-secondary" @click="openLogsModal">
            <i class="ri-history-line"></i>
            提醒紀錄
          </button>
          <button
            class="btn btn-primary"
            :disabled="reminderSaving || reminderLoading"
            @click="saveReminderSettings"
          >
            <i v-if="reminderSaving" class="ri-loader-4-line spin"></i>
            <i v-else class="ri-save-line"></i>
            {{ reminderSaving ? '儲存中...' : '儲存設定' }}
          </button>
        </div>
      </div>

      <div v-if="reminderLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入設定中...</p>
      </div>

      <template v-else>
        <div v-if="!reminderForm.annualDowngradeEnabled" class="reminder-warning">
          <i class="ri-error-warning-line"></i>
          尚未啟用「年度自動降等」。降等前提醒依附年度降等設定運作，請先至
          <router-link to="/client/settings">客戶參數設定</router-link>
          啟用年度自動降等並設定降等月份，提醒才會實際發送。
        </div>

        <div class="form-group">
          <div class="toggle-row">
            <span class="toggle-label">啟用降等前提醒</span>
            <label class="toggle-switch">
              <input v-model="reminderForm.downgradeReminderEnabled" type="checkbox" />
              <span class="toggle-slider"></span>
            </label>
            <span class="toggle-status" :class="reminderForm.downgradeReminderEnabled ? 'on' : 'off'">
              {{ reminderForm.downgradeReminderEnabled ? '已啟用' : '已停用' }}
            </span>
          </div>
        </div>

        <template v-if="reminderForm.downgradeReminderEnabled">
          <div class="form-group">
            <label class="form-label">提醒天數（結算日前 N 天）</label>
            <input
              v-model="reminderDaysText"
              type="text"
              class="form-control reminder-days-input"
              placeholder="例如：30,7（結算日前 30 天、7 天各提醒一次）"
            />
            <small class="form-text">多個天數以逗號分隔，各段各發一次、不重發</small>
            <span v-if="reminderErrors.days" class="error-text">{{ reminderErrors.days }}</span>
          </div>

          <div class="form-group reminder-notif-block">
            <label class="form-label">提醒管道與範本</label>
            <NotificationChannelSelector
              v-model:channels="reminderForm.downgradeReminderChannels"
              v-model:template-ids="reminderForm.downgradeReminderTemplateIds"
              show-default-template-preview
              template-preview-preset="tier_downgrade"
            />
            <span v-if="reminderErrors.channels" class="error-text" style="display:block;margin-top:8px;">
              {{ reminderErrors.channels }}
            </span>
          </div>
        </template>
      </template>
    </div>
    </div>

    <!-- 提醒紀錄 Modal -->
    <div v-if="logsModalVisible" class="modal-overlay" @click.self="logsModalVisible = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="ri-history-line"></i>
            降等前提醒紀錄
          </h3>
          <button class="modal-close" @click="logsModalVisible = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="logs-filters">
            <input
              v-model="logsFilters.search"
              type="text"
              class="form-control"
              placeholder="搜尋會員名稱／電話"
              @keyup.enter="fetchLogs(1)"
            />
            <input
              v-model="logsFilters.settlementDate"
              type="date"
              class="form-control"
              @change="fetchLogs(1)"
            />
            <select v-model="logsFilters.status" class="form-control" @change="fetchLogs(1)">
              <option value="">全部狀態</option>
              <option value="success">成功</option>
              <option value="failed">失敗</option>
            </select>
            <button class="btn btn-secondary" @click="fetchLogs(1)">
              <i class="ri-search-line"></i>
              查詢
            </button>
          </div>

          <div v-if="logsLoading" class="loading-state">
            <div class="spinner"></div>
            <p>載入中...</p>
          </div>

          <div v-else-if="logs.length === 0" class="logs-empty">
            <i class="ri-inbox-line"></i>
            <p>尚無提醒紀錄</p>
          </div>

          <div v-else class="table-wrapper logs-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>會員</th>
                  <th>等級</th>
                  <th>提醒段</th>
                  <th>結算日</th>
                  <th>保級差額</th>
                  <th>管道</th>
                  <th>狀態</th>
                  <th>發送時間</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in logs" :key="log.id">
                  <td>
                    <div>{{ log.memberName || '—' }}</div>
                    <small class="text-muted">{{ log.memberPhone || '' }}</small>
                  </td>
                  <td>{{ log.tierName || log.tierCode }}</td>
                  <td>前 {{ log.reminderDays }} 天</td>
                  <td>{{ log.settlementDate }}</td>
                  <td>{{ formatCurrency(log.amountToKeep) }}</td>
                  <td>
                    <span
                      v-for="ch in (log.channels || [])"
                      :key="ch.channel"
                      class="channel-chip"
                      :class="ch.sent ? 'chip-success' : 'chip-muted'"
                      :title="ch.error || ''"
                    >
                      {{ channelLabel(ch.channel) }}
                    </span>
                  </td>
                  <td>
                    <span :class="log.status === 'success' ? 'badge badge-success' : 'badge badge-danger'">
                      {{ log.status === 'success' ? '成功' : '失敗' }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(log.sentAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <ListPagination
            v-if="logsPagination.totalPages > 1"
            :page="logsPagination.page"
            :total-pages="logsPagination.totalPages"
            :total="logsPagination.totalItems"
            @change="fetchLogs"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import NotificationChannelSelector from '@/components/NotificationChannelSelector.vue';

export default {
  name: 'ClientMemberTiers',
  components: { NotificationChannelSelector },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('會員等級管理');
    const tiers = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const filterActive = ref('');

    const fetchTiers = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {};
        if (filterActive.value !== '') {
          params.isActive = filterActive.value === 'true';
        }

        const response = await memberTierService.getTiers(params);

        if (response && response.success && response.data) {
          tiers.value = response.data;
        } else {
          error.value = response?.message || '獲取會員等級列表失敗';
        }
      } catch (err) {
        console.error('獲取會員等級列表失敗:', err);
        error.value = err.message || '獲取會員等級列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleFilterChange = () => {
      fetchTiers();
    };

    const handleCreate = () => {
      router.push('/client/member-tiers/create');
    };

    const handleEdit = (tierId) => {
      router.push(`/client/member-tiers/${tierId}/edit`);
    };

    const handleDelete = async (tierId) => {
      const tier = tiers.value.find(t => t.id === tierId);
      const confirmed = await showDeleteConfirm(tier ? tier.tierName : '此等級');

      if (!confirmed) return;

      try {
        const response = await memberTierService.deleteTier(tierId);

        if (response && response.success) {
          await fetchTiers();
        } else {
          await showError('刪除失敗', response?.message || '刪除會員等級失敗');
        }
      } catch (err) {
        console.error('刪除會員等級失敗:', err);
        await showError('刪除失敗', err.message || '刪除會員等級失敗');
      }
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return 'NT$ 0';
      return `NT$ ${Number(amount).toLocaleString('zh-TW')}`;
    };

    // ==================== 降等前提醒設定 ====================

    const reminderLoading = ref(false);
    const reminderSaving = ref(false);
    const reminderDaysText = ref('30,7');
    const reminderErrors = reactive({ days: '', channels: '' });
    const reminderForm = reactive({
      annualDowngradeEnabled: false,
      downgradeReminderEnabled: false,
      downgradeReminderChannels: ['line'],
      downgradeReminderTemplateIds: {}
    });

    const loadReminderSettings = async () => {
      reminderLoading.value = true;
      try {
        const response = await clientSettingsService.get(NAMESPACES.MEMBER_TIER);
        const settings = response?.data?.settings || {};
        reminderForm.annualDowngradeEnabled = !!settings.annualDowngradeEnabled;
        reminderForm.downgradeReminderEnabled = !!settings.downgradeReminderEnabled;
        reminderForm.downgradeReminderChannels =
          Array.isArray(settings.downgradeReminderChannels) && settings.downgradeReminderChannels.length > 0
            ? settings.downgradeReminderChannels
            : ['line'];
        reminderForm.downgradeReminderTemplateIds =
          settings.downgradeReminderTemplateIds && typeof settings.downgradeReminderTemplateIds === 'object'
            ? settings.downgradeReminderTemplateIds
            : {};
        const days = Array.isArray(settings.downgradeReminderDays) ? settings.downgradeReminderDays : [30, 7];
        reminderDaysText.value = days.join(',');
      } catch (err) {
        console.error('載入降等前提醒設定失敗:', err);
        showError('載入失敗', err.message || '載入降等前提醒設定失敗');
      } finally {
        reminderLoading.value = false;
      }
    };

    const parseReminderDays = () => {
      return [...new Set(
        String(reminderDaysText.value || '')
          .split(',')
          .map((d) => parseInt(d.trim(), 10))
          .filter((d) => Number.isInteger(d) && d > 0)
      )];
    };

    const saveReminderSettings = async () => {
      reminderErrors.days = '';
      reminderErrors.channels = '';

      const days = parseReminderDays();
      if (reminderForm.downgradeReminderEnabled) {
        if (days.length === 0) {
          reminderErrors.days = '請輸入至少一個有效的提醒天數（正整數，逗號分隔）';
          return;
        }
        if (!reminderForm.downgradeReminderChannels || reminderForm.downgradeReminderChannels.length === 0) {
          reminderErrors.channels = '啟用提醒時請至少選擇一個提醒管道';
          return;
        }
      }

      reminderSaving.value = true;
      try {
        await clientSettingsService.merge(NAMESPACES.MEMBER_TIER, {
          downgradeReminderEnabled: reminderForm.downgradeReminderEnabled,
          downgradeReminderDays: days,
          downgradeReminderChannels: reminderForm.downgradeReminderChannels,
          downgradeReminderTemplateIds: reminderForm.downgradeReminderTemplateIds || {}
        });
        await showSuccess('儲存成功', '降等前提醒設定已更新');
        await loadReminderSettings();
      } catch (err) {
        console.error('儲存降等前提醒設定失敗:', err);
        showError('儲存失敗', err.message || '儲存降等前提醒設定失敗');
      } finally {
        reminderSaving.value = false;
      }
    };

    // ==================== 提醒紀錄 ====================

    const logsModalVisible = ref(false);
    const logsLoading = ref(false);
    const logs = ref([]);
    const logsFilters = reactive({ search: '', settlementDate: '', status: '' });
    const logsPagination = reactive({ page: 1, pageSize: 20, totalPages: 1, totalItems: 0 });

    const fetchLogs = async (page = 1) => {
      logsLoading.value = true;
      try {
        const params = { page, pageSize: logsPagination.pageSize };
        if (logsFilters.search) params.search = logsFilters.search;
        if (logsFilters.settlementDate) params.settlementDate = logsFilters.settlementDate;
        if (logsFilters.status) params.status = logsFilters.status;

        const response = await memberTierService.getReminderLogs(params);
        if (response?.success && response.data) {
          logs.value = response.data.items || [];
          const p = response.data.pagination || {};
          logsPagination.page = p.page || page;
          logsPagination.totalPages = p.totalPages || 1;
          logsPagination.totalItems = p.totalItems ?? p.total ?? 0;
        }
      } catch (err) {
        console.error('載入提醒紀錄失敗:', err);
        showError('載入失敗', err.message || '載入提醒紀錄失敗');
      } finally {
        logsLoading.value = false;
      }
    };

    const openLogsModal = () => {
      logsModalVisible.value = true;
      fetchLogs(1);
    };

    const channelLabel = (channel) => {
      const labels = { line: 'LINE', email: 'Email', sms: 'SMS' };
      return labels[channel] || channel;
    };

    const formatDateTime = (value) => {
      if (!value) return '—';
      return new Date(value).toLocaleString('zh-TW', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    onMounted(() => {
      fetchTiers();
      loadReminderSettings();
    });

    return {
      tiers,
      loading,
      error,
      pageTitle,
      filterActive,
      fetchTiers,
      handleFilterChange,
      handleCreate,
      handleEdit,
      handleDelete,
      formatCurrency,
      // 降等前提醒設定
      reminderLoading,
      reminderSaving,
      reminderForm,
      reminderDaysText,
      reminderErrors,
      saveReminderSettings,
      // 提醒紀錄
      logsModalVisible,
      logsLoading,
      logs,
      logsFilters,
      logsPagination,
      fetchLogs,
      openLogsModal,
      channelLabel,
      formatDateTime,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.filter-group {
  min-width: 150px;
}

/* 覆寫表格最小寬度（7 個欄位） */
.table-wrapper table {
  min-width: 860px;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  font-weight: 600;
  color: #333;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.code-badge {
  background-color: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  color: #333;
}

.tier-name {
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.error-state .error-message {
  color: #dc3545;
  margin-bottom: 12px;
}

/* ===== 降等前提醒設定 ===== */
.reminder-card {
  margin-top: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.reminder-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.reminder-card-title {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.reminder-card-desc {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.reminder-card-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.reminder-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 16px;
}

.reminder-warning i {
  font-size: 16px;
  flex-shrink: 0;
}

.reminder-warning a {
  color: #b45309;
  font-weight: 600;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #6c757d;
  font-size: 13px;
}

.error-text {
  display: block;
  color: #dc3545;
  font-size: 13px;
  margin-top: 5px;
}

.reminder-days-input {
  max-width: 400px;
}

.reminder-notif-block {
  max-width: 920px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on {
  color: #4caf50;
}

.toggle-status.off {
  color: #9ca3af;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ===== 提醒紀錄 Modal ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal-container {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 960px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.modal-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 4px;
}

.modal-close:hover {
  color: #111827;
}

.modal-body {
  padding: 16px 20px;
  overflow-y: auto;
}

.logs-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.logs-filters .form-control {
  max-width: 220px;
}

.logs-empty {
  text-align: center;
  padding: 40px 0;
  color: #9ca3af;
}

.logs-empty i {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

.logs-table-wrapper table {
  min-width: 860px;
}

.channel-chip {
  display: inline-block;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  margin-right: 4px;
  margin-bottom: 2px;
}

.chip-success {
  background: #dcfce7;
  color: #166534;
}

.chip-muted {
  background: #f3f4f6;
  color: #9ca3af;
}

.badge-danger {
  background: #fee2e2;
  color: #b91c1c;
}

.text-muted {
  color: #9ca3af;
}

.logs-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.page-info {
  font-size: 13px;
  color: #6b7280;
}

@media (max-width: 640px) {
  .reminder-card-header {
    flex-direction: column;
  }

  .logs-filters .form-control {
    max-width: 100%;
    width: 100%;
  }
}
</style>
